/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    Icon,
    Badge,
    StyledModal,
    ModalActions,
    DatePicker,
    ButtonGroup,
    Stack,
    Box,
    Text,
    Select,
    NumberField,
    ReadMoreBlock,
    TextBlock,
    Inline,
    SecurityHelper,
    FormValidationAlert,
    Alert,
    Checkbox,
    useFormatDateTime,
    STATUS as API_STATUS,
    useToast,
    useFlags,
    localToday,
    FlexRow,
    FlexCol,
    DividerNew as Divider,
    Heading,
    announce,
    Spinner,
} from 'sarsaparilla';
import { authModalActions } from 'shared-ui/auth/redux';

import useTourList from '../../hooks/useTourList';
import {
    addAvailabilityAlert,
    getAvailabilityAlertCounts,
    resetAvailabilityAlerts,
} from '../../shared/actions';
import { showAvailabilityNotification as showAvailabilityNotificationUtil } from '../../utils/launchDarklyUtils';
import { getMaxTotalGuests } from '../../utils/generalTicketUtils';
import { getSingularOrPluralText } from '../../utils/utils';
import { AVAILABILITY_ALERT_TIMED_ENTRY } from '../../shared/constants';

function AvailabilityAlerts({ tourId, isFullWidth = false }) {
    const dispatch = useDispatch();
    const formatDateTime = useFormatDateTime();
    const showToast = useToast();
    const { ticAvailNotifications } = useFlags();
    const [availabilityAlertsState, setAvailabilityAlertsState] = useState({
        isModalOpen: false,
        errors: [],
        status: API_STATUS.PENDING,
        startDate: null,
        selectTourOption: tourId || '',
        isFlexStartDateChecked: false,
        numberOfTickets: 0,
    });
    const [isLoading, setIsLoading] = useState(false);
    const accountType = useSelector((state) => state.timedEntry.accountType.accountType);
    const account = useSelector((state) => state.auth.account);
    const accountEmail = account?.account?.email ?? null;
    const accountId = account?.account?.account_id ?? null;
    const firstName = account?.account?.first_name ?? '';
    const lastName = account?.account?.last_name ?? '';
    const facilityId = useSelector((state) => state.timedEntry.facility.facility_id);
    const facilityName = useSelector((state) => state.timedEntry.facility.facility_name);
    const facilityLoadingStatus = useSelector(
        (state) => state.timedEntry.facility.loadingStatus
    );
    const facilityPurchaseMaximums = useSelector(
        (state) => state.timedEntry.facility.purchase_maximums
    );
    const facilityPurchaseMinimums = useSelector(
        (state) => state.timedEntry.facility.purchase_minimums
    );
    const facilityPurchaseLimits = useSelector(
        (state) => state.timedEntry.facility.purchase_limits
    );
    const tourLoadingStatus = useSelector(
        (state) => state.timedEntry.tours.loadingStatus
    );
    const createAvailabilityAlertStatus = useSelector(
        (state) => state.timedEntry.availabilityAlerts.availabilityAlert.status
    );
    const createAvailabilityAlertApiError = useSelector(
        (state) => state.timedEntry.availabilityAlerts.availabilityAlert.apiError
    );
    const maxAlerts = useSelector(
        (state) => state.timedEntry.availabilityAlerts.availabilityAlertsCount.max_configs
    );
    const countMap = useSelector(
        (state) =>
            state.timedEntry.availabilityAlerts.availabilityAlertsCount.asset_config_count
    );
    const totalActiveAlerts = useSelector(
        (state) =>
            state.timedEntry.availabilityAlerts.availabilityAlertsCount
                .total_active_configs
    );
    const campgroundNames = useSelector(
        (state) =>
            state.timedEntry.availabilityAlerts.availabilityAlertsCount.asset_id_to_name
    );

    const { tourList, tourMap } = useTourList(facilityId);
    const inputRefs = useRef({
        tour: null,
        startDate: null,
        flexibleStartDate: null,
        numberOfTickets: null,
    });

    const tourData = useMemo(() => {
        const data = {
            tourMap: {},
            tourIds: [],
            selectTourOptions: [{ label: 'All entries', value: 'all' }],
            toursAlertObject: [],
        };

        tourList.forEach((tour) => {
            const { tour_id: currentTourId, tour_name: currentTourName } = tour;
            data.tourMap[currentTourId] = currentTourName;
            data.tourIds.push(currentTourId);
            data.selectTourOptions.push({ label: currentTourName, value: currentTourId });
            data.toursAlertObject.push({ name: currentTourName, id: currentTourId });
        });

        return data;
    }, [tourList]);

    const selectedTour = useMemo(
        () => tourMap?.[availabilityAlertsState?.selectTourOption] ?? null,
        [availabilityAlertsState?.selectTourOption, tourMap]
    );

    const ticketsAllowed = useMemo(() => {
        const numberTicketsAllowed = {
            maxTicketsAllowed: 1,
            minTicketsAllowed: 1,
        };

        if (selectedTour) {
            const facility = {
                purchase_maximums: facilityPurchaseMaximums,
                purchase_limits: facilityPurchaseLimits,
                purchase_minimums: facilityPurchaseMinimums,
            };
            numberTicketsAllowed.maxTicketsAllowed =
                getMaxTotalGuests({
                    facility,
                    tour: selectedTour,
                    accountType,
                }) || 1;
        }

        return numberTicketsAllowed;
    }, [
        accountType,
        facilityPurchaseLimits,
        facilityPurchaseMaximums,
        facilityPurchaseMinimums,
        selectedTour,
    ]);

    const ticketsAllowedText = useMemo(() => {
        const allowedTexts = [];

        if (ticketsAllowed?.minTicketsAllowed) {
            const ticketsText = getSingularOrPluralText(
                ticketsAllowed?.minTicketsAllowed,
                'ticket',
                'tickets'
            );
            allowedTexts.push(`${ticketsAllowed?.minTicketsAllowed} ${ticketsText} min`);
        }

        if (ticketsAllowed?.maxTicketsAllowed) {
            const ticketsText = getSingularOrPluralText(
                ticketsAllowed?.maxTicketsAllowed,
                'ticket',
                'tickets'
            );
            allowedTexts.push(`${ticketsAllowed?.maxTicketsAllowed} ${ticketsText} max`);
        }

        return allowedTexts?.join(' / ');
    }, [ticketsAllowed]);

    const facilityIds = useMemo(() => {
        const countMapKeys = Object.keys(countMap || {});
        return countMapKeys?.map((key) => key) || [];
    }, [countMap]);

    const showAvailabilityNotification = useMemo(() => {
        return showAvailabilityNotificationUtil(facilityId, ticAvailNotifications);
    }, [facilityId, ticAvailNotifications]);

    const dateFormatOptions = useMemo(
        () => ({
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        }),
        []
    );

    const fullName = useMemo(
        () => `${firstName} ${lastName}`?.trim(),
        [firstName, lastName]
    );

    const flexibleDates = useMemo(() => {
        const dates = { start: '', end: '' };

        if (
            availabilityAlertsState?.isFlexStartDateChecked &&
            availabilityAlertsState?.startDate
        ) {
            dates.start = formatDateTime(
                availabilityAlertsState?.startDate?.subtract({ days: 3 }),
                dateFormatOptions
            );

            dates.end = formatDateTime(
                availabilityAlertsState?.startDate?.add({ days: 3 }),
                dateFormatOptions
            );
        }

        return dates;
    }, [
        availabilityAlertsState?.isFlexStartDateChecked,
        availabilityAlertsState?.startDate,
        dateFormatOptions,
        formatDateTime,
    ]);

    const showAddAlertSection = useMemo(
        () => totalActiveAlerts < maxAlerts,
        [maxAlerts, totalActiveAlerts]
    );

    const getInitialData = useCallback(() => {
        setIsLoading(true);
        getAvailabilityAlertCounts(accountId)(dispatch).then(() => {
            setIsLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountId]);

    useEffect(() => {
        let status = API_STATUS.PENDING;
        if (
            facilityLoadingStatus === API_STATUS.ERROR ||
            tourLoadingStatus === API_STATUS.ERROR
        ) {
            status = API_STATUS.ERROR;
        }
        if (
            facilityLoadingStatus === API_STATUS.SUCCESS &&
            tourLoadingStatus === API_STATUS.SUCCESS
        ) {
            status = API_STATUS.SUCCESS;
        }

        setAvailabilityAlertsState((prevState) => ({
            ...prevState,
            status,
        }));
    }, [facilityLoadingStatus, tourLoadingStatus]);

    const setInputRef = (key) => (ref) => {
        inputRefs.current[key] = ref;
    };

    const resetState = useCallback(() => {
        dispatch(resetAvailabilityAlerts());
        setAvailabilityAlertsState((prevState) => ({
            ...prevState,
            isModalOpen: false,
            errors: [],
            startDate: null,
            selectTourOption: tourId || '',
            isFlexStartDateChecked: false,
            numberOfTickets: 0,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tourId]);

    const validate = () => {
        setAvailabilityAlertsState((prevState) => ({ ...prevState, errors: [] }));
        const newErrors = [];

        const refs = Object.values(inputRefs?.current);

        refs?.forEach((ref) => {
            if (!ref) return;
            const validation = ref?.validate?.();

            if (validation && validation?.isInvalid) {
                newErrors.push({
                    inputId: validation?.id ?? validation?.inputId,
                    errorText: validation?.errorText,
                });
            }
        });

        if (newErrors.length > 0) {
            setAvailabilityAlertsState((prevState) => ({
                ...prevState,
                errors: newErrors,
            }));
            return false;
        }

        return true;
    };

    const buildAvailabilityAlert = useCallback(() => {
        let tourIds = [availabilityAlertsState?.selectTourOption];
        let selectedTourNames = [
            {
                id: availabilityAlertsState?.selectTourOption,
                name: tourData?.tourMap?.[availabilityAlertsState?.selectTourOption],
            },
        ];

        if (availabilityAlertsState?.selectTourOption === 'all') {
            tourIds = tourData?.tourIds;
            selectedTourNames = tourData?.toursAlertObject;
        }

        const availabilityAlert = {
            asset_id: facilityId,
            asset_type: AVAILABILITY_ALERT_TIMED_ENTRY,
            email: accountEmail,
            facility_name: facilityName,
            flex_value: availabilityAlertsState?.isFlexStartDateChecked ? 3 : 0,
            is_active: true,
            name: fullName,
            quantity: availabilityAlertsState?.numberOfTickets,
            reservation_start_date: availabilityAlertsState?.startDate?.toString(),
            secondary_ids: tourIds,
            user_id: accountId,
            notification_details_map: { selected_tour_names: selectedTourNames },
        };

        return availabilityAlert;
    }, [
        accountEmail,
        accountId,
        availabilityAlertsState?.isFlexStartDateChecked,
        availabilityAlertsState?.numberOfTickets,
        availabilityAlertsState?.selectTourOption,
        availabilityAlertsState?.startDate,
        facilityId,
        facilityName,
        fullName,
        tourData?.tourIds,
        tourData?.tourMap,
        tourData?.toursAlertObject,
    ]);

    const handleCloseModal = useCallback(() => {
        resetState();
    }, [resetState]);

    const handleOpenModal = useCallback(() => {
        if (!SecurityHelper.isLoggedIn() || !accountId) {
            dispatch(authModalActions.openLoginModal());
            return;
        }

        if (totalActiveAlerts === undefined) {
            getInitialData();
        }

        setAvailabilityAlertsState((prevState) => ({ ...prevState, isModalOpen: true }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountId]);

    const handleSaveAlert = useCallback(
        async (event) => {
            try {
                event.preventDefault();
                setAvailabilityAlertsState((prevState) => ({
                    ...prevState,
                    errors: [],
                }));

                const isValid = validate();
                if (!isValid) {
                    return;
                }
                const availabilityAlert = buildAvailabilityAlert();
                await dispatch(addAvailabilityAlert(availabilityAlert));
                getInitialData();
                handleCloseModal();
                showToast('Availability Alert Saved', { type: 'success' });
            } catch (error) {
                // No-op.
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [buildAvailabilityAlert, handleCloseModal, getInitialData]
    );

    const handleSelectTour = useCallback((e) => {
        setAvailabilityAlertsState((prevState) => ({
            ...prevState,
            selectTourOption: e.target.value,
            numberOfTickets: 0,
        }));

        announce(
            'you changed your selection to a new tour, so you need to review the number of tickets requested to match this tours ticket limits.'
        );
    }, []);

    const handleChangeDate = (date, validation) => {
        if (validation?.isInvalid) {
            return;
        }

        setAvailabilityAlertsState((prevState) => ({
            ...prevState,
            startDate: date,
        }));
    };

    const handleCheckboxChange = (e) => {
        setAvailabilityAlertsState((prevState) => ({
            ...prevState,
            isFlexStartDateChecked: e.target.checked,
        }));
    };

    const handleNumberOfTicketsChange = (value) => {
        setAvailabilityAlertsState((prevState) => ({
            ...prevState,
            numberOfTickets: value,
        }));
    };

    const renderRequestedTicketsDescription = () => {
        if (availabilityAlertsState?.selectTourOption === 'all') {
            return (
                <Text size="sm">
                    Be aware that some tours require a min / max amount of tickets. Check
                    those tours for specific rules
                </Text>
            );
        }

        if (ticketsAllowedText) {
            return <Text size="sm">{ticketsAllowedText}</Text>;
        }

        return null;
    };

    const renderActiveAlertsContent = () => {
        if (totalActiveAlerts > 0) {
            return (
                <>
                    <Stack space="sm">
                        <FlexRow>
                            <FlexCol xs={8} sm={9}>
                                <Heading headingLevel={2} appearance="h6">
                                    Saved Alerts:
                                </Heading>
                            </FlexCol>
                            <FlexCol xs={4} sm={3}>
                                <Button appearance="link" href="/account/alerts">
                                    Manage My Alerts
                                </Button>
                            </FlexCol>
                        </FlexRow>

                        <Box padding="sm">
                            {facilityIds.map((alertFacilityId) => {
                                const alertFacilityName =
                                    campgroundNames?.[alertFacilityId];
                                const alertCounter = countMap?.[alertFacilityId];
                                const unitsText = alertCounter > 1 ? 'alerts' : 'alert';

                                return (
                                    <Inline
                                        key={`availability-active-alert-${alertFacilityId}`}
                                        space="xs"
                                    >
                                        <Icon iconName="bell" aria-hidden="true" />
                                        <Text>
                                            <Text fontWeight="bold" tag="span">
                                                {alertFacilityName}
                                            </Text>
                                            <Text tag="span">{` (${alertCounter} ${unitsText})`}</Text>
                                        </Text>
                                    </Inline>
                                );
                            })}
                        </Box>

                        <Text>
                            You are allowed up to {maxAlerts} availability alerts.
                        </Text>
                    </Stack>
                    <Divider />
                </>
            );
        }

        return null;
    };

    const renderManageAlertContent = () => {
        return (
            <>
                <Stack>
                    {renderActiveAlertsContent()}
                    <Box
                        className="tickets-availability-alerts__disclaimer-box"
                        padding="xs"
                        background="gray-2"
                    >
                        <ReadMoreBlock>
                            <TextBlock>
                                <Text
                                    className="mr-half"
                                    fontWeight="semibold"
                                    tag="span"
                                >
                                    {'You have reached the maximum number of alerts. '}
                                </Text>
                                If you want to create a new one, go to Manage My Alerts to
                                remove one of your existing alerts.
                            </TextBlock>
                        </ReadMoreBlock>
                    </Box>
                </Stack>
                <ModalActions>
                    <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                        <Button appearance="tertiary" onClick={handleCloseModal}>
                            Close
                        </Button>
                        <Button href="/account/alerts">Manage My Alerts</Button>
                    </ButtonGroup>
                </ModalActions>
            </>
        );
    };

    const renderAddAlertSection = () => {
        return (
            <>
                {renderActiveAlertsContent()}
                <form noValidate onSubmit={handleSaveAlert}>
                    <Stack>
                        {totalActiveAlerts > 0 && (
                            <Heading headingLevel={2} appearance="h6">
                                Set Availability Alert
                            </Heading>
                        )}
                        <Text>Send me an email for availability that matches:</Text>
                        <Inline space="xs">
                            <Icon iconName="ticket" aria-hidden="true" />
                            <Text fontWeight="bold">{facilityName} </Text>
                        </Inline>
                        <FormValidationAlert errors={availabilityAlertsState?.errors} />
                        <Select
                            id="tour"
                            label="Entry"
                            placeholder="Choose an option"
                            ref={setInputRef('tour')}
                            isRequired
                            options={tourData?.selectTourOptions}
                            value={availabilityAlertsState?.selectTourOption}
                            onChange={handleSelectTour}
                        />

                        <Box>
                            <Stack space="xs">
                                <DatePicker
                                    label="Start Date"
                                    ref={setInputRef('startDate')}
                                    isRequired
                                    minValue={localToday}
                                    value={availabilityAlertsState?.startDate}
                                    onChange={handleChangeDate}
                                />
                                <Checkbox
                                    id="flexible-start-date"
                                    label="My start date is flexible (+/- 3 days)"
                                    innerRef={setInputRef('flexibleStartDate')}
                                    onChange={handleCheckboxChange}
                                    checked={
                                        availabilityAlertsState?.isFlexStartDateChecked
                                    }
                                />
                                {flexibleDates?.start && flexibleDates?.end && (
                                    <TextBlock>
                                        <Text
                                            className="mr-half"
                                            fontWeight="semibold"
                                            tag="span"
                                        >
                                            Potential Check In Dates:
                                        </Text>
                                        <Text tag="span">
                                            {`${flexibleDates?.start} - ${flexibleDates?.end}`}
                                        </Text>
                                    </TextBlock>
                                )}
                            </Stack>
                        </Box>
                        <Box>
                            <Stack space="xs">
                                <NumberField
                                    id="numberOfTickets"
                                    ref={setInputRef('numberOfTickets')}
                                    visualLabel="Number of tickets requested"
                                    visualLabelPosition="top"
                                    isRequired
                                    units="tickets"
                                    min={ticketsAllowed?.minTicketsAllowed}
                                    max={ticketsAllowed?.maxTicketsAllowed}
                                    value={availabilityAlertsState?.numberOfTickets}
                                    onChange={handleNumberOfTicketsChange}
                                />
                                {renderRequestedTicketsDescription()}
                            </Stack>
                        </Box>
                        <Box
                            className="tickets-availability-alerts__disclaimer-box"
                            padding="xs"
                            background="gray-2"
                        >
                            <ReadMoreBlock>
                                <TextBlock>
                                    <Text
                                        className="mr-half"
                                        fontWeight="semibold"
                                        tag="span"
                                    >
                                        *Disclaimer:
                                    </Text>
                                    Availability alerts send you an email if another
                                    ticketholder cancels their reservation, and{' '}
                                    {availabilityAlertsState.numberOfTickets} or more
                                    tickets become available. This does not alert you in
                                    the case of regularly scheduled ticket releases.
                                    <Text>
                                        This does not guarantee you a reservation - other
                                        visitors may have alerts set for the same date.
                                    </Text>
                                    <Text>
                                        Please check your alert date against ticket
                                        availability - notifications are not sent for
                                        tickets already available.
                                    </Text>
                                </TextBlock>
                            </ReadMoreBlock>
                        </Box>
                    </Stack>
                    <ModalActions>
                        <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                            <Button
                                type="button"
                                appearance="tertiary"
                                onClick={handleCloseModal}
                            >
                                Close Without Saving
                            </Button>
                            <Button
                                type="submit"
                                isLoading={createAvailabilityAlertStatus === 'LOADING'}
                            >
                                Save Alert
                            </Button>
                        </ButtonGroup>
                    </ModalActions>
                </form>
            </>
        );
    };

    const renderContent = () => {
        if (isLoading) {
            return <Spinner />;
        }

        if (!showAddAlertSection) {
            return renderManageAlertContent();
        }

        return renderAddAlertSection();
    };

    if (!showAvailabilityNotification) {
        return null;
    }

    return (
        <div className="tickets-availability-alerts">
            <Button
                className="tickets-availability-alerts__button"
                appearance="tertiary"
                iconBeforeElement={<Icon iconName="bell" />}
                shouldFitContainer={isFullWidth}
                onClick={handleOpenModal}
                isLoading={availabilityAlertsState?.status === API_STATUS.PENDING}
            >
                Set Availability Alert
                {totalActiveAlerts > 0 && (
                    <Badge
                        className="tickets-availability-alerts__button-badge"
                        appearance="primary"
                    >
                        {totalActiveAlerts}
                    </Badge>
                )}
            </Button>
            <StyledModal
                size="md"
                isOpen={availabilityAlertsState?.isModalOpen}
                heading={
                    totalActiveAlerts === 0
                        ? 'Set Availability Alert'
                        : 'Availability Alerts'
                }
                onRequestClose={handleCloseModal}
            >
                <Stack>
                    {createAvailabilityAlertStatus === 'ERROR' &&
                        createAvailabilityAlertApiError && (
                            <Alert type="error">{createAvailabilityAlertApiError}</Alert>
                        )}

                    {renderContent()}
                </Stack>
            </StyledModal>
        </div>
    );
}

AvailabilityAlerts.propTypes = { tourId: PropTypes.string, isFullWidth: PropTypes.bool };

export default AvailabilityAlerts;
